(() => {
  if (customElements.get('gr-grip-upsell')) return;

  let addInProgress = false;
  const apiRoot = () => {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  async function readCart() {
    const response = await fetch(`${apiRoot()}cart.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });
    if (!response.ok) throw new Error('Unable to check your cart. Please try again.');
    return response.json();
  }

  // Keep the existing dialog open; replace only its contents.
  function renderDrawer(html) {
    if (!html) return false;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const next = parsed.querySelector('#cart-drawer .cart-drawer__inner');
    const current = document.querySelector('#cart-drawer .cart-drawer__inner');
    if (!next || !current) return false;

    const drawer = document.querySelector('#cart-drawer');
    const panel = drawer?.querySelector('dialog');
    const focusWasInDrawer = !!panel?.contains(document.activeElement);
    current.replaceWith(next);

    if (focusWasInDrawer) {
      const target = drawer.querySelector('button[name="checkout"], a[href$="/checkout"], .theme-drawer__close-button');
      target?.focus({ preventScroll: true });
    }
    return true;
  }

  async function refreshDrawer(sectionId, initialHtml) {
    if (renderDrawer(initialHtml)) return;
    const url = new URL(apiRoot(), window.location.origin);
    url.searchParams.set('sections', sectionId);
    const response = await fetch(url, { cache: 'no-store' });
    if (!response.ok) throw new Error('Unable to refresh the cart.');
    const sections = await response.json();
    if (!renderDrawer(sections[sectionId])) throw new Error('Unable to refresh the cart.');
  }

  // Request the actual header section when its cart icon can be identified.
  // Refresh only that icon, preserving the rest of the header and its menus.
  const iconSelectors = ['cart-icon', '[ref="cartIcon"]', '[data-testid="cart-icon"]'];
  function headerCartTarget() {
    for (const selector of iconSelectors) {
      const element = document.querySelector(selector);
      const wrapper = element?.closest('[id^="shopify-section-"]');
      if (wrapper && !element.closest('#cart-drawer')) {
        return { selector, wrapperId: wrapper.id, sectionId: wrapper.id.replace('shopify-section-', '') };
      }
    }
    return null;
  }

  function refreshHeaderIcon(target, sections) {
    if (!target || !sections?.[target.sectionId]) return;
    const parsed = new DOMParser().parseFromString(sections[target.sectionId], 'text/html');
    const current = document.getElementById(target.wrapperId)?.querySelector(target.selector);
    const next = parsed.querySelector(target.selector);
    if (current && next) current.replaceWith(next);
  }

  class GRGripUpsell extends HTMLElement {
    connectedCallback() {
      this._controller?.abort();
      this._controller = new AbortController();
      const { signal } = this._controller;

      this.select = this.querySelector('[data-gr-grip-variant]');
      this.button = this.querySelector('[data-gr-grip-add]');
      this.price = this.querySelector('[data-gr-grip-price]');
      this.message = this.querySelector('[data-gr-grip-message]');
      this.image = this.querySelector('[data-gr-grip-image]');
      this.setupButtons = Array.from(this.querySelectorAll('[data-gr-grip-setup]'));
      const data = this.querySelector('[data-gr-grip-data]');
      if (!this.select || !this.button || !this.price || !this.message || !data) return;

      try {
        this.product = JSON.parse(data.textContent);
        // Defence in depth: standalone Spring Set is never an upsell option.
        this.product.variants = this.product.variants.filter(variant =>
          ['GR Bell only', 'Anti-Dragging Set'].includes(variant.setup)
        );
      } catch {
        this.button.disabled = true;
        this.showMessage('Please open the product page to select GR GripRacing.');
        return;
      }

      this.defaultImage = this.image?.getAttribute('src') || '';
      this.setup = this.product.variants.some(variant => variant.setup === 'GR Bell only')
        ? 'GR Bell only' : 'Anti-Dragging Set';

      this.setupButtons.forEach(button => {
        button.addEventListener('click', () => {
          if (addInProgress) return;
          const nextSetup = button.dataset.grGripSetup;
          if (!['GR Bell only', 'Anti-Dragging Set'].includes(nextSetup)) return;
          const previousModel = this.selectedVariant()?.model;
          this.setup = nextSetup;
          this.showMessage('');
          this.refreshModels(previousModel);
          this.updateSelection();
        }, { signal });
      });
      this.select.addEventListener('change', () => {
        this.showMessage('');
        this.updateSelection();
      }, { signal });
      this.button.addEventListener('click', () => this.addSelectedVariant(), { signal });
      this.refreshModels();
      this.updateSelection();
    }

    disconnectedCallback() {
      this._controller?.abort();
    }

    setupVariants() {
      return this.product?.variants.filter(variant => variant.setup === this.setup) || [];
    }

    selectedVariant() {
      return this.setupVariants().find(variant => String(variant.id) === this.select.value);
    }

    refreshModels(previousModel) {
      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = 'Select scooter model';
      const options = [placeholder];
      const variants = this.setupVariants();

      variants.forEach(variant => {
        const option = document.createElement('option');
        option.value = String(variant.id);
        option.textContent = `${variant.model} — ${variant.priceText}${variant.available ? '' : ' (Sold out)'}`;
        option.disabled = !variant.available;
        options.push(option);
      });
      this.select.replaceChildren(...options);
      const match = previousModel && variants.find(variant => variant.model === previousModel && variant.available);
      this.select.value = match ? String(match.id) : '';
    }

    updateSelection() {
      const variants = this.setupVariants();
      const available = variants.filter(variant => variant.available);
      const variant = this.selectedVariant();
      const priceVariants = available.length ? available : variants;
      const lowest = priceVariants.reduce((min, candidate) => !min || candidate.price < min.price ? candidate : min, null);
      const priceVaries = lowest && priceVariants.some(candidate => candidate.price !== lowest.price);

      if (variant) this.price.textContent = variant.priceText;
      else if (lowest) this.price.textContent = `${priceVaries ? 'From ' : ''}${lowest.priceText}`;
      else this.price.textContent = 'Unavailable for this setup';

      this.setupButtons.forEach(button => {
        const setup = button.dataset.grGripSetup;
        const selected = setup === this.setup;
        button.classList.toggle('is-active', selected);
        button.setAttribute('aria-pressed', String(selected));
        button.disabled = addInProgress || !this.product.variants.some(candidate => candidate.setup === setup);
      });
      this.select.disabled = addInProgress || !available.length;
      this.button.disabled = addInProgress || !variant?.available;
      this.button.textContent = addInProgress ? 'ADDING...' :
        (!available.length ? 'CURRENTLY SOLD OUT' :
          (variant?.available ? 'ADD GR GRIPRACING TO CART' : 'SELECT A SCOOTER MODEL'));

      if (this.image) {
        const imageVariant = variant || available[0] || variants[0];
        const url = imageVariant?.imageUrl || this.defaultImage;
        if (url && this.image.getAttribute('src') !== url) this.image.setAttribute('src', url);
        this.image.alt = `${this.setup} — GR GripRacing`;
      }
    }

    showMessage(text) {
      this.message.textContent = text;
      this.message.hidden = !text;
    }

    async addSelectedVariant() {
      const variant = this.selectedVariant();
      if (addInProgress || !variant?.available) return;

      addInProgress = true;
      this.showMessage('');
      this.updateSelection();
      let added = false;
      let alreadyPresent = false;
      const sectionId = this.dataset.sectionId;
      const productId = String(this.dataset.productId);

      try {
        // Recheck after the offer was rendered to avoid adding a duplicate.
        const cart = await readCart();
        alreadyPresent = cart.items.some(item => String(item.product_id) === productId);
        if (alreadyPresent) {
          await refreshDrawer(sectionId);
          return;
        }

        const headerTarget = headerCartTarget();
        const sectionIds = [sectionId];
        if (headerTarget && !sectionIds.includes(headerTarget.sectionId)) sectionIds.push(headerTarget.sectionId);

        const response = await fetch(`${apiRoot()}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: variant.id, quantity: 1 }],
            sections: sectionIds,
            sections_url: apiRoot()
          })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'Unable to add this variant. Please try again.');
        added = true;

        await refreshDrawer(sectionId, result.sections?.[sectionId]);
        refreshHeaderIcon(headerTarget, result.sections);
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      } catch (error) {
        if (added || alreadyPresent) {
          // The cart changed successfully. Never encourage a second add.
          this.showMessage('The GR GripRacing is in your cart. Refresh this page to see the updated cart.');
        } else {
          this.showMessage(error.message || 'Unable to add this variant. Please try again.');
        }
      } finally {
        addInProgress = false;
        document.querySelectorAll('gr-grip-upsell').forEach(offer => offer.updateSelection());
        if ((added || alreadyPresent) && this.isConnected) {
          this.select.disabled = true;
          this.setupButtons.forEach(button => { button.disabled = true; });
          this.button.disabled = true;
          this.button.textContent = 'GR GRIPRACING IS IN YOUR CART';
        }
      }
    }
  }

  customElements.define('gr-grip-upsell', GRGripUpsell);
})();
